import * as LDClient from 'launchdarkly-js-client-sdk';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { FeatureFlag, FeatureFlagValue } from './feature-flags.js';

export type LDClientTypes = {
    context: LDClient.LDContext;
    envKey: string;
    streaming?: boolean;
};

const FEATURE_FLAGS_LOCAL_STORAGE_KEY = 'featureFlags';

const dispatchFeatureFlagsUpdatedInLocalStorageEvent = () => {
    window.dispatchEvent(
        new StorageEvent('local-storage', {
            key: FEATURE_FLAGS_LOCAL_STORAGE_KEY,
        }),
    );
};

export const launchDarklyClientSide = async ({
    envKey,
    context,
    streaming = false,
}: LDClientTypes): Promise<LDClient.LDClient> => {
    console.log('initialising ldclient');
    const client = LDClient.initialize(envKey, context, {
        disableSyncEventPost: true,
        streaming,
        sendEvents: true,
        fetchGoals: false,
    });

    return client.waitUntilReady().then(() => {
        localStorage.setItem('featureFlags', JSON.stringify(client.allFlags()));

        client.on('change', (changeSet: LDClient.LDFlagChangeset) => {
            const flagSet: LDClient.LDFlagSet = {};
            for (const key in changeSet) {
                flagSet[key] = changeSet[key].current;
            }
            const previous = JSON.parse(localStorage.getItem('featureFlags') || '{}');
            localStorage.setItem(
                FEATURE_FLAGS_LOCAL_STORAGE_KEY,
                JSON.stringify({ ...previous, ...flagSet }),
            );
            dispatchFeatureFlagsUpdatedInLocalStorageEvent();
        });

        dispatchFeatureFlagsUpdatedInLocalStorageEvent();
        return client;
    });
};

export function useFeatureFlag<T extends FeatureFlagValue = boolean>(flag?: FeatureFlag): T {
    const [featureFlags] = useLocalStorage<Record<string, FeatureFlagValue>>(
        FEATURE_FLAGS_LOCAL_STORAGE_KEY,
        {},
    );

    const getFlagValueOrDefault = useCallback(() => {
        if (!flag) {
            return false as T;
        }
        return featureFlags[flag.key] ?? flag.defaultValue;
    }, [featureFlags, flag]);

    return getFlagValueOrDefault() as T;
}
